import { useState } from 'react'
import {
  PostAttachment,
  PostEntityType,
  createPost,
  updatePost,
  uploadPostAttachments,
} from '../../api/posts'
import PostMarkdownEditor from './PostMarkdownEditor'

interface Props {
  entityType: PostEntityType
  entityId: number
  onPublished: () => void
}

export default function PostEditor({ entityType, entityId, onPublished }: Props) {
  const [text, setText] = useState('')
  const [draftPostId, setDraftPostId] = useState<number | null>(null)
  const [attachments, setAttachments] = useState<PostAttachment[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  const ensureDraftPost = async () => {
    if (draftPostId) return draftPostId
    const created = await createPost(entityType, entityId, text || '')
    setDraftPostId(created.id)
    return created.id
  }

  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return

    try {
      setIsUploading(true)
      const postId = await ensureDraftPost()
      const uploaded = await uploadPostAttachments(postId, files)
      setAttachments((prev) => [...prev, ...uploaded])
    } catch (error) {
      console.error('❌ Не удалось загрузить файлы:', error)
      alert(`Не удалось загрузить файлы: ${error instanceof Error ? error.message : 'неизвестная ошибка'}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handlePublish = async () => {
    try {
      setIsSubmitting(true)
      if (draftPostId) {
        await updatePost(draftPostId, text)
      } else {
        await createPost(entityType, entityId, text)
      }

      setText('')
      setDraftPostId(null)
      setAttachments([])
      onPublished()
    } catch (error) {
      console.error('❌ Не удалось опубликовать пост:', error)
      alert('Не удалось опубликовать пост')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <div className="font-medium text-gray-900 mb-2">Новый пост</div>
      <PostMarkdownEditor
        value={text}
        onChange={setText}
        attachments={attachments}
        onFilesPicked={handleFileUpload}
        isUploading={isUploading}
      />

      <div className="mt-3 flex items-center gap-3">
        <button
          className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          onClick={handlePublish}
          disabled={isSubmitting || isUploading || !text.trim()}
        >
          {isSubmitting ? 'Публикую...' : 'Опубликовать пост'}
        </button>
      </div>
    </div>
  )
}
